/**
 * useViewScope / ViewScopeProvider — global "viewing as" preference.
 *
 * Default: "self" for everyone. Owner/admin can switch to "org",
 * department heads can switch to "team". DB-backed via user_preferences
 * with localStorage as a synchronous cache for api() header attachment.
 */
import { createContext, useContext, useEffect, useRef, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { usePreference, readPreference } from './use-preference';

export type ScopeLevel = 'self' | 'team' | 'org';

interface ViewScopeContextValue {
  scope: ScopeLevel;
  setScope: (s: ScopeLevel) => void;
}

const Ctx = createContext<ViewScopeContextValue | null>(null);

export function ViewScopeProvider({ children }: { children: ReactNode }) {
  const [scope, setScopeRaw] = usePreference<ScopeLevel>('view_scope', 'self');

  const setScope = (s: ScopeLevel) => {
    setScopeRaw(s);
    window.dispatchEvent(new CustomEvent('waypoint:scope-changed', { detail: s }));
  };

  // A fresh browser paints with the localStorage default ('self') and every
  // query fires with that header; when the server preference hydrates to 'org'
  // the cached answers ("0 at risk", "Quiet day") would stand until a reload.
  // Any change after mount — hydration or the picker — refetches everything
  // but the preferences themselves.
  const qc = useQueryClient();
  const prev = useRef(scope);
  useEffect(() => {
    window.dispatchEvent(new CustomEvent('waypoint:scope-changed', { detail: scope }));
    if (prev.current === scope) return;
    prev.current = scope;
    void qc.invalidateQueries({ predicate: (q) => q.queryKey[0] !== 'user-preferences' });
  }, [scope, qc]);

  return (
    <Ctx.Provider value={{ scope, setScope }}>
      {children}
    </Ctx.Provider>
  );
}

export function useViewScope(): ViewScopeContextValue {
  const ctx = useContext(Ctx);
  if (!ctx) {
    return { scope: 'self', setScope: () => {} };
  }
  return ctx;
}

/** Read the current scope synchronously — used by api() before it has access to React context. */
export function readCurrentScope(): ScopeLevel {
  const v = readPreference<string>('view_scope', 'self');
  if (v === 'self' || v === 'team' || v === 'org') return v;
  return 'self';
}
